import styled from '@emotion/styled';
import { Prismic, PrismicContext } from '@landsbankinn/prismic';
import { shouldForwardProp, Text } from '@landsbankinn/ui';
import { useRouter } from 'next/router';
import React, { forwardRef, useContext, useState } from 'react';
import type { FC } from 'react';
import { animated } from 'react-spring';
import { Img, ImgAr } from '../../components/img/Img';
import type { ImgSource } from '../../components/img/Img';

export const CardHostLink = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<Partial<CardProps>>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: auto;
  cursor: pointer;
  padding-left: 24px;
  padding-top: 14px;
  padding-bottom: 14px;
  padding-right: 21px;
  margin-bottom: 16px;
  background-color: ${(props) => {
    return Prismic.text(props.backgroundColor);
  }};
`;

const ImgContainer = styled.div`
  min-width: 60px;

  > *:first-child {
    height: 100%;
  }
`;

export interface CardProps {
  link?: Prismic.Link;
  title: Prismic.RichText;
  image?: ImgSource;
  action?: React.ReactElement;
  backgroundColor?: Prismic.RichText;
  imageRef?:
    | React.MutableRefObject<HTMLImageElement | undefined>
    | React.ForwardedRef<unknown>;
  ref?: React.ForwardedRef<HTMLAnchorElement & HTMLDivElement>;

  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export const AukakronurCard: FC<CardProps> = forwardRef<
  HTMLAnchorElement & HTMLDivElement,
  CardProps
>(function CardFn(props: CardProps, ref) {
  const {
    link,
    title,
    image,
    backgroundColor,
    imageRef,
    onMouseEnter,
    onMouseLeave,
    onClick,
    ...rest
  } = props;

  const router = useRouter();
  const { urlResolver } = useContext(PrismicContext);
  const url = Prismic.link(urlResolver, link);

  const [hover, setHover] = useState(false);

  const onEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    setHover(true);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };

  const onLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    setHover(false);
    if (onMouseLeave) {
      onMouseLeave(e);
    }
  };

  const onCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (window?.getSelection?.()?.type === 'Range') {
      e.preventDefault();
      return false;
    }

    if (onClick) {
      onClick(e);
    }

    if (url.onClick) {
      return url.onClick(e);
    }

    return router.push(url.href ?? '', url.as);
  };

  return (
    <CardHostLink
      backgroundColor={backgroundColor}
      ref={ref}
      {...rest}
      onMouseLeave={onLeave}
      onMouseEnter={onEnter}
      onClick={onCardClick}
    >
      <Text variant={'h3'}>{Prismic.text(title)}</Text>

      <ImgContainer>
        <Img
          src={image}
          ar={'ar_1_1'}
          aspectRatio={1}
          percentage={100}
          ref={imageRef}
        />
      </ImgContainer>
    </CardHostLink>
  );
});
